import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "../components/marketing/MarketingLayout";
import {
  Shield,
  Lock,
  Eye,
  EyeOff,
  Server,
  Database,
  Key,
  Cloud,
  CheckCircle2,
  XCircle,
  GitBranch,
  ArrowRight,
  ExternalLink,
  Trash2,
  Timer,
  BookOpen,
  Network,
  Container,
  MonitorCheck,
  Globe,
  FileText,
} from "lucide-react";
import { Card, CardContent } from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";
import { Separator } from "../components/shadcn/separator";

export default function Security() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative mx-auto max-w-5xl px-6 py-28 md:py-36 text-center">
          <Badge variant="outline" className="mb-6 inline-flex items-center gap-2.5 rounded-full border-brand/30 bg-brand/10 px-5 py-2 text-sm text-brand">
            <Shield className="h-4 w-4" />
            Security &amp; Trust
          </Badge>
          <h1 className="mb-6 font-display text-4xl font-extrabold text-text md:text-5xl lg:text-6xl">
            Security &amp;{" "}
            <span className="bg-gradient-to-r from-brand via-emerald-400 to-focus bg-clip-text text-transparent">Trust</span>
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-muted mb-6">
            How Paxley protects your code and data
          </p>
          <p className="mx-auto max-w-3xl text-muted leading-relaxed">
            Paxley is built with a security-first architecture. We designed every component around a
            simple principle: minimize data exposure while maximizing security insight. Your source code
            is never stored, credentials are never persisted, and access is always read-only.
          </p>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Data Handling Model */}
      <section className="py-28 md:py-36">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-20">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-brand mb-4">Data Handling</p>
            <h2 className="font-display text-4xl font-extrabold text-text md:text-5xl mb-5">
              Your code, <span className="bg-gradient-to-r from-brand to-focus bg-clip-text text-transparent">your control</span>
            </h2>
            <p className="text-muted text-lg max-w-xl mx-auto">
              We handle your code with the minimum access required and never retain it beyond the scan.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Trash2,
                title: "No Persistent Code Storage",
                description:
                  "Code is cloned temporarily for scanning and immediately deleted. Only finding metadata (file paths, line numbers, small code snippets) is stored.",
              },
              {
                icon: Timer,
                title: "Short-Lived Access Tokens",
                description:
                  "GitHub App installation tokens expire in 1 hour. Paxley never stores long-lived credentials.",
              },
              {
                icon: Eye,
                title: "Read-Only Access",
                description:
                  "The GitHub App has read-only code access. It cannot push, delete, or modify your repositories.",
              },
              {
                icon: Key,
                title: "Instant Revocation",
                description:
                  "Uninstall the GitHub App from your org settings to immediately revoke all access.",
              },
            ].map(({ icon: Icon, title, description }) => (
              <Card key={title} className="group h-full border-border/30 bg-surface1 hover:border-border/50 transition-colors">
                <CardContent className="p-6">
                  <div className="mb-5 h-12 w-12 rounded-xl bg-brand/10 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mb-3 font-display text-lg font-bold text-text">{title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Infrastructure Security */}
      <section className="py-28 md:py-36 bg-surface1/30">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-20">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-brand mb-4">Infrastructure</p>
            <h2 className="font-display text-4xl font-extrabold text-text md:text-5xl mb-5">
              Enterprise-grade{" "}
              <span className="bg-gradient-to-r from-brand to-focus bg-clip-text text-transparent">infrastructure</span>
            </h2>
            <p className="text-muted text-lg max-w-xl mx-auto">
              Built on Azure with defense-in-depth security at every layer.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Cloud,
                title: "Azure Container Apps",
                description:
                  "Hosted on SOC 2 and ISO 27001 compliant Azure infrastructure with automatic scaling and high availability.",
              },
              {
                icon: Database,
                title: "Encrypted Database",
                description:
                  "Azure PostgreSQL Flexible Server with encryption at rest (AES-256) and automated backups.",
              },
              {
                icon: Lock,
                title: "TLS 1.2+ Everywhere",
                description:
                  "All connections encrypted in transit. No plaintext communication between any services.",
              },
              {
                icon: Key,
                title: "Azure Key Vault",
                description:
                  "Secrets stored in Azure Key Vault (FIPS 140-2 Level 2 validated HSMs). No secrets in environment variables or code.",
              },
              {
                icon: Network,
                title: "Network Isolation",
                description:
                  "Azure Virtual Network isolation between services. Internal components are not exposed to the public internet.",
              },
              {
                icon: Server,
                title: "Dedicated Compute",
                description:
                  "No shared tenancy. Dedicated compute per customer scan ensures complete isolation between organizations.",
              },
            ].map(({ icon: Icon, title, description }) => (
              <Card key={title} className="group h-full border-border/30 bg-surface1 hover:border-border/50 transition-colors">
                <CardContent className="p-6">
                  <div className="mb-5 h-12 w-12 rounded-xl bg-brand/10 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mb-3 font-display text-lg font-bold text-text">{title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* What We Store vs What We Don't */}
      <section className="py-28 md:py-36">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-20">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-brand mb-4">Data Transparency</p>
            <h2 className="font-display text-4xl font-extrabold text-text md:text-5xl mb-5">
              What we store vs{" "}
              <span className="bg-gradient-to-r from-brand to-focus bg-clip-text text-transparent">what we do not</span>
            </h2>
            <p className="text-muted text-lg max-w-xl mx-auto">
              Full transparency on exactly what data Paxley retains after a scan.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* What We Store */}
            <Card className="border-border/30 bg-surface1 h-full">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-brand" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-text">What We Store</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Finding metadata (title, severity, file path, line number)",
                    "Small code snippets (2-4KB for AI remediation context)",
                    "SBOM / dependency lists",
                    "Scan timestamps and statistics",
                    "Organization and user profile (from GitHub OAuth)",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                      <span className="text-text2">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* What We Never Store */}
            <Card className="border-border/30 bg-surface1 h-full">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-text">What We Never Store</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Full source code",
                    "Git credentials or tokens",
                    "User passwords",
                    "Raw secrets or credentials found by scanner (redacted)",
                    "Repository contents after scan completion",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm">
                      <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-text2">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Scanning Architecture */}
      <section className="py-28 md:py-36 bg-surface1/30">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-20">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-brand mb-4">Architecture</p>
            <h2 className="font-display text-4xl font-extrabold text-text md:text-5xl mb-5">
              Scanning{" "}
              <span className="bg-gradient-to-r from-brand to-focus bg-clip-text text-transparent">architecture</span>
            </h2>
            <p className="text-muted text-lg max-w-xl mx-auto">
              A secure pipeline from webhook to dashboard. Code is never persisted.
            </p>
          </div>
          <div className="space-y-4">
            {[
              {
                step: "01",
                icon: GitBranch,
                title: "Webhook Trigger",
                description: "GitHub App webhook triggers a scan when a push or pull request is detected.",
              },
              {
                step: "02",
                icon: Container,
                title: "Clone with Short-Lived Token",
                description: "Worker clones the repository using a short-lived installation token (expires in 1 hour).",
              },
              {
                step: "03",
                icon: Shield,
                title: "Multi-Engine Analysis",
                description: "Scanners analyze code across SAST, SCA, Secrets, and IaC engines in isolated containers.",
              },
              {
                step: "04",
                icon: FileText,
                title: "Normalize and Store Findings",
                description: "Results are normalized, deduplicated, and stored as finding metadata in the database.",
              },
              {
                step: "05",
                icon: Trash2,
                title: "Immediate Deletion",
                description: "Source code directory is deleted immediately after scan completion. No code is retained.",
              },
              {
                step: "06",
                icon: MonitorCheck,
                title: "Dashboard and PR Comments",
                description: "Findings are displayed in the dashboard and posted as PR comments for developer review.",
              },
            ].map(({ step, icon: Icon, title, description }, i) => (
              <Card key={step} className="border-border/30 bg-surface1 hover:border-border/50 transition-colors">
                <CardContent className="flex items-start gap-5 p-6">
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <span className="font-mono text-2xl font-extrabold text-brand/30">{step}</span>
                    <div className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-brand" />
                    </div>
                  </div>
                  <div className="pt-1">
                    <h3 className="font-display text-lg font-bold text-text mb-1">{title}</h3>
                    <p className="text-sm text-muted leading-relaxed">{description}</p>
                  </div>
                  {i < 5 && (
                    <ArrowRight className="h-5 w-5 text-brand/20 mt-3 ml-auto flex-shrink-0 hidden lg:block" />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Compliance & Certifications */}
      <section className="py-28 md:py-36">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-20">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-brand mb-4">Compliance</p>
            <h2 className="font-display text-4xl font-extrabold text-text md:text-5xl mb-5">
              Compliance &amp;{" "}
              <span className="bg-gradient-to-r from-brand to-focus bg-clip-text text-transparent">certifications</span>
            </h2>
            <p className="text-muted text-lg max-w-xl mx-auto">
              Meeting industry standards for data protection and security coverage.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Shield,
                title: "SOC 2 Type II",
                status: "In Progress",
                description:
                  "Service Organization Control audit for security, availability, and confidentiality.",
              },
              {
                icon: Globe,
                title: "GDPR Compliant",
                status: "Active",
                description:
                  "Full compliance with EU General Data Protection Regulation requirements.",
              },
              {
                icon: Database,
                title: "Data Residency",
                status: "Azure East US",
                description:
                  "Default data residency in Azure East US. Configurable for enterprise customers.",
              },
              {
                icon: BookOpen,
                title: "OWASP Top 10",
                status: "Full Coverage",
                description:
                  "Scanning engines cover all OWASP Top 10 vulnerability categories and beyond.",
              },
            ].map(({ icon: Icon, title, status, description }) => (
              <Card key={title} className="group h-full border-border/30 bg-surface1 hover:border-border/50 transition-colors">
                <CardContent className="p-6">
                  <div className="mb-5 h-12 w-12 rounded-xl bg-brand/10 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mb-1 font-display text-lg font-bold text-text">{title}</h3>
                  <Badge variant="outline" className="mb-3 border-brand/20 bg-brand/10 text-[10px] font-bold text-brand uppercase tracking-wider">
                    {status}
                  </Badge>
                  <p className="text-sm text-muted leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Self-Hosted Option */}
      <section className="py-28 md:py-36 bg-surface1/30">
        <div className="mx-auto max-w-4xl px-6">
          <Card className="border-border/30 bg-surface1">
            <CardContent className="p-10 md:p-14 text-center">
              <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10">
                <Server className="h-7 w-7 text-brand" />
              </div>
              <h2 className="mb-4 font-display text-3xl font-extrabold text-text md:text-4xl">Self-Hosted Option</h2>
              <p className="mx-auto max-w-2xl text-muted text-lg leading-relaxed mb-8">
                For organizations that require complete control, Paxley can be deployed in your own
                infrastructure using Docker. Your code never leaves your network.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild>
                  <Link
                    to="/docs"
                    className="text-sm px-6 py-3 rounded-xl inline-flex items-center gap-2"
                  >
                    View Documentation <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="secondary" asChild>
                  <a
                    href="mailto:yash.sancheti@paxleysoftware.com?subject=Paxley%20sales"
                    className="text-sm px-6 py-3 rounded-xl inline-flex items-center gap-2"
                  >
                    Contact Sales <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Separator className="mx-auto max-w-4xl bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Contact */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="relative text-center">
          <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10">
            <EyeOff className="h-7 w-7 text-brand" />
          </div>
          <h2 className="mb-4 text-3xl font-extrabold text-text md:text-4xl lg:text-5xl">Have security questions?</h2>
          <p className="mb-10 text-muted text-lg max-w-lg mx-auto">
            We are happy to answer any questions about our security practices, data handling, or compliance posture.
          </p>
          <Button size="lg" asChild>
            <a
              href="mailto:yash.sancheti@paxleysoftware.com?subject=Security%20question"
              className="text-base px-10 py-4 rounded-xl inline-flex items-center gap-2"
            >
              Email our security team <ArrowRight className="h-5 w-5" />
            </a>
          </Button>
        </div>
      </section>

    </MarketingLayout>
  );
}
